import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import FileUpload from '../shared/FileUpload';
import { useToast } from '../ui/Toast';

const PHASES = ['initiation','planning','execution','close_out'];
const DEFAULT_REQUIRED_DOCS = JSON.stringify([
  { document_type: 'sor', title: 'Statement of Requirements', required: true, notes: 'Must be uploaded before SOW generation' },
  { document_type: 'sow', title: 'Statement of Work', required: true, notes: 'Auto-generated or manually uploaded' },
], null, 2);

export default function SOWTemplatePage() {
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [sowTypes, setSowTypes]   = useState([]); // [{ value, label }]
  const [selected, setSelected]   = useState(null);   // { ...template, revisions: [] }
  const [showCreate, setShowCreate] = useState(false);
  const [editingRev, setEditingRev] = useState(null);  // revision being edited/created
  const [saving, setSaving]         = useState(false);
  const [viewingRev, setViewingRev] = useState(null);  // full revision detail for display

  const sowTypeLabel = (code) => sowTypes.find(t => t.value === code)?.label || code;

  const load = () => api.get('sow-templates').then(setTemplates).catch(() => {});
  const loadDetail = (id) => api.get(`sow-templates/${id}`).then(t => { setSelected(t); setViewingRev(t.revisions?.[0] || null); }).catch(() => {});

  useEffect(() => {
    load();
    api.get('config-options', { category: 'sow_type' }).then(d => setSowTypes(d || [])).catch(() => {});
  }, []);

  const createTemplate = async (form) => {
    setSaving(true);
    try {
      const t = await api.post('sow-templates', form);
      await load();
      loadDetail(t.id);
      setShowCreate(false);
    } catch (e) { toast.error('Failed: ' + e.message); }
    setSaving(false);
  };

  const saveRevision = async (form) => {
    setSaving(true);
    try {
      if (editingRev?.id) {
        await api.put(`sow-templates/${selected.id}/revisions/${editingRev.id}`, form);
      } else {
        await api.post(`sow-templates/${selected.id}/revisions`, form);
      }
      await loadDetail(selected.id);
      setEditingRev(null);
    } catch (e) { toast.error('Failed: ' + e.message); }
    setSaving(false);
  };

  const toggleActive = async (t) => {
    await api.put(`sow-templates/${t.id}`, { is_active: t.is_active ? 0 : 1 });
    load();
    if (selected?.id === t.id) loadDetail(t.id);
  };

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">SOW Templates</h2>
          <div className="text-[9px] text-gray-500">Define reusable templates per SOW type with milestones, resources, tasks and AI generation instructions</div>
        </div>
        <button onClick={() => setShowCreate(true)} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">+ New Template</button>
      </div>

      <CoveragePanel sowTypes={sowTypes} templates={templates} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Template list */}
        <div className="bg-white border border-gray-200 rounded overflow-hidden">
          <div className="px-3 py-2 border-b bg-gray-50 text-[8px] font-bold uppercase text-gray-500">Templates ({templates.length})</div>
          {templates.length === 0 && <div className="p-4 text-[9px] text-gray-400 text-center">No templates yet</div>}
          {templates.map(t => (
            <div key={t.id} onClick={() => loadDetail(t.id)} className={`px-3 py-2 border-b border-gray-100 cursor-pointer hover:bg-gold/5 ${selected?.id === t.id ? 'bg-gold/10 border-l-2 border-l-gold' : ''} ${!t.is_active ? 'opacity-40' : ''}`}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-semibold text-navy">{t.name}</div>
                <span className="text-[7px] font-bold px-1.5 py-0.5 bg-navy/10 text-navy rounded">{sowTypeLabel(t.sow_type)}</span>
              </div>
              <div className="flex items-center justify-between mt-0.5">
                <div className="text-[8px] text-gray-400">{t.description || '—'}</div>
                <div className="flex items-center gap-1">
                  {t.latest_file_revision ? (
                    <span className="text-[7px] font-bold px-1 py-0.5 bg-emerald-100 text-emerald-700 rounded" title={t.latest_template_file_name || 'Word template attached'}>.docx</span>
                  ) : (
                    <span className="text-[7px] font-bold px-1 py-0.5 bg-amber-100 text-amber-700 rounded" title="No Word template uploaded — AI generation will fall back to Markdown">no .docx</span>
                  )}
                  {t.latest_revision && <span className="text-[7px] text-gray-400">v{t.latest_revision}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Template detail */}
        <div className="col-span-2">
          {!selected ? (
            <div className="bg-white border border-gray-200 rounded p-8 text-center text-[10px] text-gray-400">Select a template to view revisions</div>
          ) : (
            <div className="space-y-3">
              {/* Header */}
              <div className="bg-white border border-gray-200 rounded p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[13px] font-extrabold text-navy">{selected.name}</div>
                    <div className="text-[9px] text-gray-500 mt-0.5">Type: <span className="font-semibold">{sowTypeLabel(selected.sow_type)}</span> · {selected.description || '—'}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => toggleActive(selected)} className={`px-2 py-1 text-[9px] font-semibold rounded ${selected.is_active ? 'bg-gray-100 text-gray-600' : 'bg-emerald-100 text-emerald-700'}`}>
                      {selected.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button onClick={() => setEditingRev({ template_id: selected.id, milestones_json: '[]', resource_lines_json: '[]', project_tasks_json: '[]', hardware_json: '[]', required_documents_json: DEFAULT_REQUIRED_DOCS, ai_prompt: '', change_notes: '', template_file_url: '', template_file_key: '', template_file_name: '', template_file_size: 0 })} className="px-2 py-1 text-[9px] font-semibold bg-navy text-white rounded">+ New Revision</button>
                  </div>
                </div>
              </div>

              {/* Revision list */}
              <div className="bg-white border border-gray-200 rounded overflow-hidden">
                <div className="px-3 py-2 border-b bg-gray-50 text-[8px] font-bold uppercase text-gray-500">Revision History</div>
                {(selected.revisions || []).length === 0 && <div className="p-4 text-[9px] text-gray-400 text-center">No revisions yet — create one above</div>}
                {(selected.revisions || []).map(r => (
                  <div key={r.id} onClick={() => setViewingRev(r)} className={`px-3 py-2 border-b border-gray-100 cursor-pointer hover:bg-gold/5 ${viewingRev?.id === r.id ? 'bg-gold/5 border-l-2 border-l-gold' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-navy">v{r.revision}</span>
                        {r === selected.revisions[0] && <span className="text-[7px] font-bold px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">Latest</span>}
                        <span className="text-[9px] text-gray-500">{r.change_notes || '—'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[8px] text-gray-400">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : '—'}</span>
                        <button onClick={e => { e.stopPropagation(); setEditingRev({ ...r }); }} className="px-2 py-0.5 text-[8px] bg-gray-100 text-gray-600 rounded hover:bg-gray-200">Edit</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Revision detail */}
              {viewingRev && <RevisionDetail revision={viewingRev} />}
              <PlaceholderReference />
            </div>
          )}
        </div>
      </div>

      {showCreate && <CreateTemplateModal sowTypes={sowTypes} onClose={() => setShowCreate(false)} onSave={createTemplate} saving={saving} />}
      {editingRev  && <RevisionEditorModal revision={editingRev} onClose={() => setEditingRev(null)} onSave={saveRevision} saving={saving} />}
    </div>
  );
}

function RevisionDetail({ revision: r }) {
  const parse = (s) => { try { return JSON.parse(s || '[]'); } catch { return []; } };
  const milestones  = parse(r.milestones_json);
  const resources   = parse(r.resource_lines_json);
  const tasks       = parse(r.project_tasks_json);
  const hardware    = parse(r.hardware_json);
  const docs        = parse(r.required_documents_json);

  // Probe the .docx for placeholders so the admin can see what the AI will fill
  const [phInfo, setPhInfo] = useState(null);
  useEffect(() => {
    setPhInfo(null);
    if (r?.template_file_url && r?.template_id && r?.id) {
      api.get(`sow-templates/${r.template_id}/revisions/${r.id}/placeholders`)
        .then(setPhInfo).catch(() => setPhInfo({ error: 'failed_to_inspect' }));
    }
  }, [r?.id, r?.template_file_url]);

  return (
    <div className="bg-white border border-gray-200 rounded p-3 space-y-3 text-[9px]">
      <div className="text-[8px] font-bold uppercase text-gray-400">Revision v{r.revision} Detail</div>

      <div>
        <div className="text-[7.5px] font-bold uppercase text-gray-400 mb-1">Word Template (.docx)</div>
        {r.template_file_url ? (
          <a href={r.template_file_url} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded text-[9px] hover:bg-blue-100">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <span className="font-semibold">{r.template_file_name || 'template.docx'}</span>
            <span className="text-[7px] text-blue-500">↓ download</span>
          </a>
        ) : (
          <div className="text-[8px] text-amber-600 italic">No Word template uploaded — AI generation will build a fully-formatted Word document from scratch (proper headings, bullets, tables) without your branding.</div>
        )}

        {r.template_file_url && phInfo && (
          phInfo.error ? (
            <div className="mt-2 text-[8px] text-gray-400 italic">Couldn't inspect template ({phInfo.error}).</div>
          ) : (phInfo.placeholders?.length === 0 && phInfo.loops?.length === 0) ? (
          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-[8px] text-blue-800">
              <div className="font-bold mb-0.5">ℹ No <code>{'{placeholder}'}</code> tokens — auto-inject mode</div>
              <div>OPS will scan this template for recognisable section headings (Executive Summary, Scope of Work, etc.) and automatically inject AI-generated content into them, preserving your branding, fonts, and layout. Only if <em>no</em> headings are found will it fall back to a generic unstyled document. You don't need to add tokens manually unless you want precise control over placement.</div>
            </div>
          ) : (
            <div className="mt-2 p-2 bg-emerald-50 border border-emerald-100 rounded">
              <div className="text-[7.5px] font-bold uppercase text-emerald-700 mb-1">Detected placeholders ({phInfo.placeholders.length})</div>
              <div className="font-mono text-[8px] text-emerald-800 break-all">{phInfo.placeholders.map(p => `{${p}}`).join(' ')}</div>
              {phInfo.loops?.length > 0 && (
                <>
                  <div className="text-[7.5px] font-bold uppercase text-emerald-700 mt-1.5 mb-1">Loops ({phInfo.loops.length})</div>
                  <div className="font-mono text-[8px] text-emerald-800 break-all">{phInfo.loops.map(p => `{#${p}}…{/${p}}`).join(' ')}</div>
                </>
              )}
            </div>
          )
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Section title={`Milestones (${milestones.length})`}>
          {milestones.length === 0 ? <Empty /> : milestones.map((m,i) => (
            <div key={i} className="flex items-baseline gap-2 py-0.5 border-b border-gray-50">
              <span className="font-mono font-bold text-navy text-[8px]">{m.milestone_code}</span>
              <span className="text-gray-700">{m.description}</span>
            </div>
          ))}
        </Section>

        <Section title={`Resource Lines (${resources.length})`}>
          {resources.length === 0 ? <Empty /> : resources.map((r,i) => (
            <div key={i} className="flex items-baseline gap-2 py-0.5 border-b border-gray-50">
              <span className="font-semibold text-navy">{r.band_name || r.band_code}</span>
              <span className="text-gray-500">{r.days}d</span>
              {r.work_package && <span className="px-1 py-0 bg-gray-100 text-gray-500 rounded text-[7px]">{r.work_package}</span>}
            </div>
          ))}
        </Section>

        <Section title={`Project Tasks (${tasks.length})`}>
          {tasks.length === 0 ? <Empty /> : tasks.map((t,i) => (
            <div key={i} className="flex items-baseline gap-2 py-0.5 border-b border-gray-50">
              <span className="text-[7px] px-1 py-0 rounded bg-blue-50 text-blue-600">{t.phase}</span>
              <span className="text-gray-700">{t.name}</span>
              <span className="text-gray-400 text-[7px]">{t.duration_days}d</span>
            </div>
          ))}
        </Section>

        <Section title={`Required Documents (${docs.length})`}>
          {docs.length === 0 ? <Empty /> : docs.map((d,i) => (
            <div key={i} className="flex items-baseline gap-2 py-0.5 border-b border-gray-50">
              {d.required && <span className="text-red-500 text-[8px] font-bold">*</span>}
              <span className="font-semibold uppercase text-[8px]">{d.document_type}</span>
              <span className="text-gray-500">{d.title}</span>
            </div>
          ))}
        </Section>
      </div>

      {r.ai_prompt && (
        <div>
          <div className="text-[7.5px] font-bold uppercase text-gray-400 mb-1">AI Generation Instructions</div>
          <div className="bg-purple-50 border border-purple-100 rounded p-2 text-[8px] text-purple-800 whitespace-pre-wrap max-h-32 overflow-y-auto">{r.ai_prompt}</div>
        </div>
      )}
      {!r.ai_prompt && (
        <div className="text-[8px] text-gray-400 italic">No custom AI instructions — will use default SOW generation prompt.</div>
      )}
    </div>
  );
}

function CoveragePanel({ sowTypes, templates }) {
  if (!sowTypes.length) return null;
  const norm = (s) => (s || '').trim().toLowerCase();
  // A template matches a SOW type if its sow_type column equals either the option's
  // value or its label (case-insensitive). Templates may have been created with
  // either string historically.
  const status = sowTypes.map(t => {
    const equiv = new Set([norm(t.value), norm(t.label)]);
    const matches = templates.filter(x => x.is_active && equiv.has(norm(x.sow_type)));
    const withFile = matches.find(x => x.latest_file_revision != null);
    return {
      label: t.label,
      value: t.value,
      hasTemplate: matches.length > 0,
      hasFile: !!withFile,
      templateName: (withFile || matches[0])?.name || null,
    };
  });
  const missingFile = status.filter(s => !s.hasFile);
  if (missingFile.length === 0) {
    return (
      <div className="mb-3 p-2 bg-emerald-50 border border-emerald-200 rounded text-[9px] text-emerald-700">
        ✓ Every SOW type has a Word template attached. AI generation will produce a real .docx for any SOW.
      </div>
    );
  }
  return (
    <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded">
      <div className="text-[9px] font-bold text-amber-800 mb-1">SOW types missing a Word template</div>
      <div className="flex flex-wrap gap-1">
        {status.map(s => (
          <span key={s.value} className={`text-[8px] px-1.5 py-0.5 rounded font-semibold ${s.hasFile ? 'bg-emerald-100 text-emerald-700' : s.hasTemplate ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}
            title={s.hasFile ? `${s.templateName} has a .docx` : s.hasTemplate ? `${s.templateName} exists but no .docx` : 'No template at all'}>
            {s.label} {s.hasFile ? '✓' : s.hasTemplate ? '· no .docx' : '· missing'}
          </span>
        ))}
      </div>
      <div className="mt-1 text-[7.5px] text-gray-400">Types with no .docx template uploaded will get a freshly built Word document without your branding.</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[7.5px] font-bold uppercase text-gray-400 mb-1">{title}</div>
      <div className="space-y-0">{children}</div>
    </div>
  );
}
function Empty() { return <div className="text-[8px] text-gray-400 italic">None defined</div>; }

const PLACEHOLDER_COVER = [
  ['{client_name}',      'Client name'],
  ['{client_contact}',   'Client contact person'],
  ['{sow_reference}',    'SOW reference / number'],
  ['{sow_name}',         'SOW name / title'],
  ['{sow_type}',         'SOW type code'],
  ['{commercial_type}',  'Fixed Price / T&M / Support'],
  ['{date_today}',       'Document date (dd Mon yyyy)'],
  ['{prepared_by}',      'Prepared by'],
  ['{document_version}', 'Document version label'],
  ['{valid_until}',      'Valid until date'],
  ['{start_date}',       'Expected start date'],
  ['{end_date}',         'Expected completion date'],
  ['{total_value}',      'Total SOW value (£)'],
];

const PLACEHOLDER_SECTIONS = [
  ['{executive_summary}',    'Executive Summary / Introduction / Overview'],
  ['{sales_pitch}',          'Why Alton Valley / About Us'],
  ['{scope_of_work}',        'Scope of Work / Scope of Services'],
  ['{objectives}',           'Objectives'],
  ['{deliverables}',         'Deliverables'],
  ['{approach}',             'Approach / Methodology'],
  ['{assumptions}',          'Assumptions'],
  ['{dependencies}',         'Dependencies'],
  ['{out_of_scope}',         'Out of Scope / Exclusions'],
  ['{acceptance_criteria}',  'Acceptance Criteria'],
  ['{payment_terms}',        'Payment Terms / Commercials'],
  ['{project_management}',   'Project Management / Governance'],
  ['{risks_and_mitigations}','Risks & Mitigations / Risk Register'],
  ['{terms_conditions}',     'Terms & Conditions'],
];

const PLACEHOLDER_TEXT_BLOCKS = [
  ['{milestones_text}',       'Milestones as plain text (auto-injected under Milestones heading)'],
  ['{resources_text}',        'Resources as plain text (auto-injected under Resource Plan heading)'],
  ['{hardware_text}',         'Hardware as plain text (auto-injected under Hardware heading)'],
  ['{revision_history_text}', 'Revision history as plain text'],
];

const PLACEHOLDER_LOOPS = [
  {
    tag: 'milestones',
    note: 'one row per milestone',
    fields: ['{index}', '{code}', '{description}', '{status}', '{forecast_date}', '{value}'],
  },
  {
    tag: 'resources',
    note: 'one row per resource line',
    fields: ['{index}', '{band_name}', '{band_code}', '{days}', '{day_rate}', '{line_total}', '{work_package}', '{description}'],
  },
  {
    tag: 'hardware',
    note: 'one row per item',
    fields: ['{index}', '{description}', '{quantity}', '{category}'],
  },
  {
    tag: 'revision_history',
    note: 'one row per issued version',
    fields: ['{version}', '{date}', '{author}', '{notes}'],
  },
];

function PlaceholderReference() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-gray-200 rounded overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-gray-50">
        <div>
          <span className="text-[9px] font-bold text-gray-600">Placeholder reference</span>
          <span className="ml-2 text-[8px] text-gray-400">— all tokens available in your .docx template</span>
        </div>
        <span className="text-[9px] text-gray-400">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="p-3 border-t space-y-4 text-[9px]">
          <div className="p-2 bg-blue-50 border border-blue-100 rounded text-[8px] text-blue-800 space-y-0.5">
            <div className="font-bold">Three ways to use placeholders</div>
            <div><span className="font-semibold">1. Explicit tokens</span> — insert <code className="font-mono bg-white px-0.5 rounded">{'{token}'}</code> directly in your .docx body text where you want content placed.</div>
            <div><span className="font-semibold">2. Cover table auto-fill</span> — in a two-column table, put the label in the left cell (e.g. "Client") and OPS will fill the right cell automatically.</div>
            <div><span className="font-semibold">3. Section heading auto-inject</span> — use a recognised heading (e.g. "Scope of Work") and OPS injects AI content below it without needing any token.</div>
          </div>

          <div>
            <div className="text-[7.5px] font-bold uppercase text-blue-600 mb-1.5">Cover / header table fields</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
              {PLACEHOLDER_COVER.map(([token, desc]) => (
                <div key={token} className="flex items-baseline gap-1.5 min-w-0">
                  <code className="text-[8px] font-mono text-blue-700 bg-blue-50 px-1 rounded shrink-0">{token}</code>
                  <span className="text-[7.5px] text-gray-500 truncate">{desc}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[7.5px] font-bold uppercase text-purple-600 mb-1.5">Body section tokens</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
              {PLACEHOLDER_SECTIONS.map(([token, desc]) => (
                <div key={token} className="flex items-baseline gap-1.5 min-w-0">
                  <code className="text-[8px] font-mono text-purple-700 bg-purple-50 px-1 rounded shrink-0">{token}</code>
                  <span className="text-[7.5px] text-gray-500 truncate">{desc}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[7.5px] font-bold uppercase text-amber-600 mb-1.5">Table / list text blocks</div>
            <div className="space-y-0.5">
              {PLACEHOLDER_TEXT_BLOCKS.map(([token, desc]) => (
                <div key={token} className="flex items-baseline gap-1.5">
                  <code className="text-[8px] font-mono text-amber-700 bg-amber-50 px-1 rounded shrink-0">{token}</code>
                  <span className="text-[7.5px] text-gray-500">{desc}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[7.5px] font-bold uppercase text-emerald-600 mb-1">Loop tokens (docxtemplater)</div>
            <div className="text-[7.5px] text-gray-400 mb-1.5">Wrap a table row with <code className="font-mono bg-gray-100 px-0.5 rounded">{'{#tag}'}</code> … <code className="font-mono bg-gray-100 px-0.5 rounded">{'{/tag}'}</code> to repeat for each item.</div>
            <div className="space-y-2">
              {PLACEHOLDER_LOOPS.map(loop => (
                <div key={loop.tag} className="bg-gray-50 rounded p-2">
                  <div className="flex items-center gap-2 mb-1">
                    <code className="text-[8px] font-mono text-emerald-700 bg-emerald-50 px-1 rounded">{`{#${loop.tag}}`}…{`{/${loop.tag}}`}</code>
                    <span className="text-[7.5px] text-gray-400">{loop.note}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {loop.fields.map(f => (
                      <code key={f} className="text-[7.5px] font-mono text-gray-600 bg-white border border-gray-200 px-1 rounded">{f}</code>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateTemplateModal({ sowTypes, onClose, onSave, saving }) {
  const [form, setForm] = useState({ name: '', sow_type: sowTypes[0]?.value || '', description: '' });
  return (
    <Modal title="New SOW Template" onClose={onClose} footer={
      <>
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={() => onSave(form)} disabled={saving || !form.name || !form.sow_type} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">{saving ? 'Saving…' : 'Create'}</button>
      </>
    }>
      <div className="space-y-3">
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Template Name *</label>
          <input autoFocus className="form-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Support Contract Standard" />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">SOW Type *</label>
          <select className="form-select" value={form.sow_type} onChange={e => setForm(p => ({ ...p, sow_type: e.target.value }))}>
            <option value="">Select type…</option>
            {sowTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Description</label>
          <input className="form-input" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Optional description" />
        </div>
      </div>
    </Modal>
  );
}

function RevisionEditorModal({ revision: r, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    milestones_json:         r.milestones_json || '[]',
    resource_lines_json:     r.resource_lines_json || '[]',
    project_tasks_json:      r.project_tasks_json || '[]',
    hardware_json:           r.hardware_json || '[]',
    required_documents_json: r.required_documents_json || DEFAULT_REQUIRED_DOCS,
    ai_prompt:               r.ai_prompt || '',
    change_notes:            r.change_notes || '',
    template_file_url:       r.template_file_url  || '',
    template_file_key:       r.template_file_key  || '',
    template_file_name:      r.template_file_name || '',
    template_file_size:      r.template_file_size || 0,
  });
  const [activeTab, setActiveTab] = useState('file');
  const [jsonError, setJsonError] = useState({});

  const setJson = (key, val) => {
    setForm(p => ({ ...p, [key]: val }));
    try { JSON.parse(val); setJsonError(p => ({ ...p, [key]: null })); }
    catch (e) { setJsonError(p => ({ ...p, [key]: e.message })); }
  };

  const anyError = Object.values(jsonError).some(Boolean);

  const isNew = !r.id;
  const tabs = [
    { id: 'file',        label: 'Word Template',  key: null },
    { id: 'milestones',  label: 'Milestones',  key: 'milestones_json' },
    { id: 'resources',   label: 'Resources',   key: 'resource_lines_json' },
    { id: 'tasks',       label: 'Tasks',       key: 'project_tasks_json' },
    { id: 'hardware',    label: 'Hardware',    key: 'hardware_json' },
    { id: 'documents',   label: 'Documents',   key: 'required_documents_json' },
    { id: 'ai',          label: 'AI Prompt',   key: 'ai_prompt' },
  ];

  const JSON_EXAMPLES = {
    milestones_json:         `[\n  { "milestone_code": "AVM-01", "description": "Project Kick-off", "sort_order": 1 },\n  { "milestone_code": "AVM-02", "description": "Installation Complete", "sort_order": 2 }\n]`,
    resource_lines_json:     `[\n  { "band_code": "G6", "band_name": "Senior Technical Specialist", "day_rate": 950, "days": 5, "work_package": "Delivery" }\n]`,
    project_tasks_json:      `[\n  { "name": "Site Survey", "phase": "initiation", "duration_days": 1 },\n  { "name": "Configuration", "phase": "execution", "duration_days": 3 }\n]`,
    hardware_json:           `[\n  { "description": "Cisco Switch", "quantity": 2, "category": "Networking" }\n]`,
    required_documents_json: `[\n  { "document_type": "sor", "title": "Statement of Requirements", "required": true },\n  { "document_type": "sow", "title": "Statement of Work", "required": true }\n]`,
  };

  return (
    <Modal title={isNew ? 'New Revision' : `Edit Revision v${r.revision}`} onClose={onClose} wide footer={
      <>
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={() => onSave(form)} disabled={saving || anyError} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">
          {saving ? 'Saving…' : isNew ? 'Create Revision' : 'Save Changes'}
        </button>
      </>
    }>
      <div className="space-y-3">
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Change Notes</label>
          <input className="form-input" value={form.change_notes} onChange={e => setForm(p => ({ ...p, change_notes: e.target.value }))} placeholder="What changed in this revision…" autoFocus />
        </div>

        <div className="border border-gray-200 rounded overflow-hidden">
          <div className="flex border-b border-gray-200 bg-gray-50">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} className={`px-3 py-1.5 text-[9px] font-semibold border-b-2 -mb-px ${activeTab === t.id ? 'border-navy text-navy bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                {t.label}
                {t.key !== 'ai_prompt' && jsonError[t.key] && <span className="ml-1 text-red-500">!</span>}
              </button>
            ))}
          </div>

          <div className="p-3">
            {activeTab === 'file' ? (
              <div className="space-y-2">
                <div className="text-[8px] text-gray-500">
                  Upload the actual Word document (<code>.docx</code>) you want the AI to fill in. Use placeholders inside the doc like <code>{'{client_name}'}</code>, <code>{'{executive_summary}'}</code> — Claude will populate them based on the SOR. For repeating sections use docxtemplater syntax: <code>{'{#milestones}{code}: {description}{/milestones}'}</code>.
                </div>
                <FileUpload
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  label="Drop a .docx template here or click to browse"
                  value={form.template_file_name}
                  onUpload={(res) => setForm(p => ({ ...p, template_file_url: res.url, template_file_key: res.key, template_file_name: res.filename, template_file_size: res.size || 0 }))}
                  onClear={() => setForm(p => ({ ...p, template_file_url: '', template_file_key: '', template_file_name: '', template_file_size: 0 }))}
                />
                {form.template_file_url && (
                  <a href={form.template_file_url} target="_blank" rel="noopener noreferrer"
                     className="text-[8px] text-blue-600 hover:underline">↓ Download current template to verify</a>
                )}
                <details className="bg-gray-50 border border-gray-200 rounded p-2 text-[8px]">
                  <summary className="cursor-pointer font-bold text-gray-600">Supported placeholders</summary>
                  <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-gray-600 font-mono">
                    <span>{'{client_name}'}</span><span>{'{sow_reference}'}</span>
                    <span>{'{sow_type}'}</span><span>{'{sow_title}'}</span>
                    <span>{'{date_today}'}</span><span>{'{prepared_by}'}</span>
                    <span>{'{valid_until}'}</span><span>{'{start_date}'}</span>
                    <span>{'{end_date}'}</span><span>{'{total_value}'}</span>
                    <span>{'{executive_summary}'}</span><span>{'{scope_of_work}'}</span>
                    <span>{'{objectives}'}</span><span>{'{deliverables}'}</span>
                    <span>{'{approach}'}</span><span>{'{assumptions}'}</span>
                    <span>{'{dependencies}'}</span><span>{'{out_of_scope}'}</span>
                    <span>{'{acceptance_criteria}'}</span><span>{'{payment_terms}'}</span>
                    <span>{'{project_management}'}</span><span>{'{risks_and_mitigations}'}</span>
                    <span>{'{terms_conditions}'}</span><span>{'{total_days}'}</span>
                  </div>
                  <div className="mt-2 text-gray-600">
                    <strong>Loops:</strong>
                    <pre className="bg-white border border-gray-200 rounded p-1.5 mt-1 text-[7.5px]">{`{#milestones}{code}: {description}{/milestones}
{#resources}{band_name}: {days} days @ {day_rate}{/resources}
{#hardware}{description} x{quantity}{/hardware}`}</pre>
                  </div>
                </details>
              </div>
            ) : activeTab === 'ai' ? (
              <div>
                <div className="text-[8px] text-gray-500 mb-2">Custom instructions prepended to the Claude prompt when generating a SOW for this template. Leave blank to use the default professional SOW generation prompt.</div>
                <textarea
                  className="form-input font-mono text-[9px] h-40 resize-y"
                  value={form.ai_prompt}
                  onChange={e => setForm(p => ({ ...p, ai_prompt: e.target.value }))}
                  placeholder="e.g. You are generating a Support Contract SOW for an enterprise IT services company. Focus on SLA definitions, response times, and support tiers..."
                />
              </div>
            ) : (() => {
              const tab = tabs.find(t => t.id === activeTab);
              const key = tab?.key;
              return (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[8px] text-gray-500">Edit as JSON array. Each element becomes a row when the template is applied.</div>
                    {JSON_EXAMPLES[key] && (
                      <button onClick={() => setJson(key, JSON_EXAMPLES[key])} className="text-[8px] text-gold hover:underline">Load example</button>
                    )}
                  </div>
                  <textarea
                    className={`form-input font-mono text-[9px] h-48 resize-y ${jsonError[key] ? 'border-red-300' : ''}`}
                    value={form[key]}
                    onChange={e => setJson(key, e.target.value)}
                    spellCheck={false}
                  />
                  {jsonError[key] && <div className="text-[8px] text-red-500 mt-1">JSON error: {jsonError[key]}</div>}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </Modal>
  );
}
